/**
 * Workflow Execution Service - Phase 5
 * 
 * Service for executing workflows, managing execution state, and handling
 * node execution logic, decision evaluation, parallel execution, and error handling.
 */

import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  getDocs,
  addDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import workflowService from './workflowService';
import { taskService } from './taskService';
import { buildEmptyExecutionTrust, populateLearningCheckResult } from './executionTrustService';
import { alertService } from './alertService';
import { surfaceTypeService } from './surfaceTypeService';

/**
 * Generate unique execution code
 */
async function generateExecutionCode() {
  try {
    const year = new Date().getFullYear();
    const executionsRef = collection(db, 'workflow_executions');
    
    // Get count of executions for this year
    const q = query(
      executionsRef,
      where('execution_code', '>=', `WFE-${year}-000`),
      where('execution_code', '<', `WFE-${year + 1}-000`),
      orderBy('execution_code', 'desc'),
      limit(1)
    );
    
    const snapshot = await getDocs(q);
    let nextNumber = 1;
    
    if (!snapshot.empty) {
      const lastCode = snapshot.docs[0].data().execution_code;
      const lastNumber = parseInt(lastCode.split('-')[2]) || 0;
      nextNumber = lastNumber + 1;
    }
    
    return `WFE-${year}-${String(nextNumber).padStart(3, '0')}`;
  } catch (error) {
    console.error('Error generating execution code:', error);
    // Fallback to timestamp-based code
    return `WFE-${Date.now()}`;
  }
}

/**
 * Create workflow execution
 */
export async function createWorkflowExecution(workflowDefinitionId, context, userId) {
  try {
    // Load workflow definition
    const workflowDefinition = await workflowService.getWorkflowDefinition(workflowDefinitionId);
    
    if (!workflowDefinition) {
      throw new Error(`Workflow definition not found: ${workflowDefinitionId}`);
    }
    
    // Generate execution code
    const execution_code = await generateExecutionCode();
    
    // Find start node
    const startNode = workflowDefinition.nodes?.find(node => node.node_type === 'start');
    
    if (!startNode) {
      throw new Error('Workflow definition must have a start node');
    }
    
    // Resolve human-readable "started by" label
    const triggerSource = context.triggered_by || 'manual';
    let startedByLabel;
    if (triggerSource === 'event' || triggerSource === 'scheduled') {
      startedByLabel = `system:${context.event_type || triggerSource}`;
    } else {
      try {
        const userSnap = await getDoc(doc(db, 'users', userId));
        if (userSnap.exists()) {
          const d = userSnap.data();
          startedByLabel = d.full_name || d.displayName || d.display_name || d.name || d.email || userId;
        } else {
          startedByLabel = userId;
        }
      } catch {
        startedByLabel = userId;
      }
    }

    // Create execution document
    const executionData = {
      execution_code,
      workflow_definition_id: workflowDefinitionId,
      workflow_name: workflowDefinition.workflow_name || workflowDefinition.name || workflowDefinitionId,
      workflow_version: workflowDefinition.workflow_version || '1.0.0',
      
      // Trigger information
      triggered_by: triggerSource,
      trigger_details: {
        user_id: userId,
        event_type: context.event_type,
        event_data: context.event_data || {},
        scheduled_time: context.scheduled_time || null
      },
      
      // Context
      patient_id: context.patient_id || null,
      location_id: context.location_id || null,
      department_id: context.department_id || null,
      organization_id: context.organization_id || context.organizationId || null,

      // Execution state
      status: 'pending',
      current_node_id: startNode.node_id,
      current_node_name: startNode.node_name || startNode.label || startNode.node_id,
      
      // Node execution history
      node_executions: [],
      
      // Workflow variables
      variables: initializeVariables(workflowDefinition.workflow_variables || [], context),
      
      // Risk adaptations
      risk_adaptations_applied: [],
      
      // Timing
      started_at: null,
      completed_at: null,
      duration_minutes: 0,
      estimated_completion: null,
      
      // Outcomes
      outcome: null,
      outcome_details: null,
      
      // Approval tracking
      approvals: [],

      execution_trust: buildEmptyExecutionTrust(0),
      confidence_score: {
        value: null,
        version: 'v1',
        calculated_at: null,
        components: {
          layer_1_score: null,
          layer_2_score: null,
          layer_1_weight: 0.6,
          layer_2_weight: 0.4
        },
        flags: []
      },

      // Escalations
      escalations: [],
      
      // Audit
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
      created_by: userId,
      started_by: userId,
      started_by_label: startedByLabel,
      execution_log: [{
        timestamp: new Date(), // Use regular Date instead of serverTimestamp() (not supported in arrays)
        event: 'execution_created',
        details: {
          workflow_definition_id: workflowDefinitionId,
          triggered_by: context.triggered_by || 'manual',
          user_id: userId
        }
      }]
    };
    
    const executionRef = doc(collection(db, 'workflow_executions'));
    await setDoc(executionRef, executionData);
    
    console.log(`✅ Created workflow execution: ${executionRef.id} (${execution_code})`);
    
    return {
      id: executionRef.id,
      ...executionData
    };
  } catch (error) {
    console.error('Error creating workflow execution:', error);
    throw error;
  }
}

/**
 * Initialize workflow variables
 */
function initializeVariables(workflowVariables, context) {
  const variables = {};
  
  // Initialize from workflow definition defaults
  workflowVariables.forEach(variable => {
    variables[variable.name] = variable.default_value || null;
  });
  
  // Override with context values
  if (context.variables) {
    Object.assign(variables, context.variables);
  }
  
  // Add context data as variables
  if (context.patient_id) variables.patient_id = context.patient_id;
  if (context.location_id) variables.location_id = context.location_id;
  if (context.department_id) variables.department_id = context.department_id;
  
  return variables;
}

/**
 * Start workflow execution
 */
export async function startWorkflowExecution(executionId, userId) {
  try {
    const executionRef = doc(db, 'workflow_executions', executionId);
    const executionDoc = await getDoc(executionRef);
    
    if (!executionDoc.exists()) {
      throw new Error(`Execution not found: ${executionId}`);
    }
    
    const execution = { id: executionDoc.id, ...executionDoc.data() };
    
    if (execution.status !== 'pending') {
      throw new Error(`Cannot start execution in status: ${execution.status}`);
    }
    
    // Update execution status
    await updateDoc(executionRef, {
      status: 'running',
      started_at: serverTimestamp(),
      updated_at: serverTimestamp(),
      execution_log: [
        ...(execution.execution_log || []),
        {
          timestamp: new Date(), // Use regular Date instead of serverTimestamp() (not supported in arrays)
          event: 'execution_started',
          details: { user_id: userId }
        }
      ]
    });
    
    // Execute start node
    await executeNode(executionId, execution.current_node_id, userId);
    
    console.log(`✅ Started workflow execution: ${executionId}`);
    
    return await getWorkflowExecution(executionId);
  } catch (error) {
    console.error('Error starting workflow execution:', error);
    throw error;
  }
}

/**
 * Get workflow execution
 */
export async function getWorkflowExecution(executionId) {
  try {
    const executionRef = doc(db, 'workflow_executions', executionId);
    const executionDoc = await getDoc(executionRef);
    
    if (!executionDoc.exists()) {
      return null;
    }
    
    return {
      id: executionDoc.id,
      ...executionDoc.data()
    };
  } catch (error) {
    console.error('Error getting workflow execution:', error);
    throw error;
  }
}

/**
 * Get all workflow executions
 */
export async function getAllWorkflowExecutions(filters = {}) {
  try {
    const executionsRef = collection(db, 'workflow_executions');
    let q = query(executionsRef);
    
    // Apply filters
    if (filters.workflow_definition_id) {
      q = query(q, where('workflow_definition_id', '==', filters.workflow_definition_id));
    }
    
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    
    if (filters.created_by) {
      q = query(q, where('created_by', '==', filters.created_by));
    }
    
    // Order by created_at descending
    q = query(q, orderBy('created_at', 'desc'));
    
    if (filters.limit) {
      q = query(q, limit(filters.limit));
    }
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting workflow executions:', error);
    throw error;
  }
}

/**
 * Execute node
 */
export async function executeNode(executionId, nodeId, userId) {
  try {
    const execution = await getWorkflowExecution(executionId);
    const workflowDefinition = await workflowService.getWorkflowDefinition(execution.workflow_definition_id);
    
    // Find node in workflow definition
    const node = workflowDefinition.nodes?.find(n => n.node_id === nodeId);
    
    if (!node) {
      throw new Error(`Node not found: ${nodeId}`);
    }
    
    // Create node execution record
    const nodeExecution = {
      node_id: node.node_id,
      node_name: node.node_name,
      node_type: node.node_type,
      started_at: new Date(), // Use regular Date instead of serverTimestamp() (not supported in arrays)
      completed_at: null,
      duration_seconds: 0,
      status: 'running',
      retry_count: 0,
      retry_history: []
    };
    
    // Add to node_executions array
    const executionRef = doc(db, 'workflow_executions', executionId);
    await updateDoc(executionRef, {
      node_executions: [...(execution.node_executions || []), nodeExecution],
      current_node_id: nodeId,
      current_node_name: node.node_name || node.label || nodeId,
      updated_at: serverTimestamp(),
      execution_log: [
        ...(execution.execution_log || []),
        {
          timestamp: new Date(), // Use regular Date instead of serverTimestamp() (not supported in arrays)
          event: 'node_execution_started',
          details: {
            node_id: nodeId,
            node_type: node.node_type,
            user_id: userId
          }
        }
      ]
    });
    
    // Execute based on node type with retry logic
    let result;
    const maxRetries = node.configuration?.max_retries || 0;
    const retryOnFailure = node.configuration?.retry_on_failure || false;
    let attempt = 0;
    let lastError = null;
    
    while (attempt <= maxRetries) {
      try {
        switch (node.node_type) {
          case 'start':
            result = await executeStartNode(execution, node, userId);
            break;
          case 'task':
            result = await executeTaskNode(execution, node, userId, workflowDefinition);
            break;
          case 'decision':
            result = await executeDecisionNode(execution, node, userId);
            break;
          case 'parallel':
            result = await executeParallelNode(execution, node, userId);
            break;
          case 'end':
            result = await executeEndNode(execution, node, userId);
            break;
          case 'integration':
            result = await executeIntegrationNode(execution, node, userId);
            break;
          case 'notification':
            result = await executeNotificationNode(execution, node, userId);
            break;
          case 'atp_check':
            result = await executeATPCheckNode(execution, node, userId);
            break;
          default:
            throw new Error(`Unknown node type: ${node.node_type}`);
        }
        
        // Success - break out of retry loop
        break;
      } catch (error) {
        lastError = error;
        attempt++;
        
        if (!retryOnFailure || attempt > maxRetries) {
          throw error;
        }
        
        // Exponential backoff: wait 2^attempt seconds before retry
        const waitTime = Math.pow(2, attempt) * 1000;
        console.log(`Retrying node execution (attempt ${attempt}/${maxRetries}) after ${waitTime}ms...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        
        // Update retry history
        const executionRef = doc(db, 'workflow_executions', executionId);
        const currentExecution = await getWorkflowExecution(executionId);
        const nodeExecutions = currentExecution.node_executions || [];
        if (nodeExecutions.length > 0) {
          const lastExecution = nodeExecutions[nodeExecutions.length - 1];
          lastExecution.retry_count = attempt;
          lastExecution.retry_history = [
            ...(lastExecution.retry_history || []),
            {
              attempt: attempt,
              timestamp: new Date(), // Use regular Date instead of serverTimestamp() (not supported in arrays)
              result: 'retrying',
              error: error.message
            }
          ];
          
          await updateDoc(executionRef, {
            node_executions: nodeExecutions,
            updated_at: serverTimestamp()
          });
        }
      }
    }
    
    if (lastError && !result) {
      throw lastError;
    }
    
    // Update node execution with result
    const updatedExecution = await getWorkflowExecution(executionId);
    const nodeExecutions = updatedExecution.node_executions || [];
    const nodeExecutionIndex = nodeExecutions.length - 1;
    
    nodeExecutions[nodeExecutionIndex] = {
      ...nodeExecutions[nodeExecutionIndex],
      ...result,
      completed_at: result.waiting_for_tasks ? null : new Date(),
      status: result.error ? 'failed' : (result.waiting_for_tasks ? 'waiting' : 'completed')
    };
    
    await updateDoc(executionRef, {
      node_executions: nodeExecutions,
      updated_at: serverTimestamp()
    });
    
    // Move to next node if not failed and not waiting for tasks
    if (!result.error && result.next_node_id) {
      await moveToNextNode(executionId, result.next_node_id, userId);
    } else if (result.waiting_for_tasks) {
      // Task node spawned tasks — execution stays 'running' at this node.
      // checkAndResumeWorkflowExecution() will advance it once all tasks complete.
      console.log(`⏳ Workflow execution ${executionId} waiting for tasks at node ${nodeId}`);
    }
    
    return result;
  } catch (error) {
    console.error('Error executing node:', error);
    const isPermissionDenied = error?.code === 'permission-denied' || error?.code === 'permission_denied';
    if (isPermissionDenied) {
      throw error;
    }
    // Update node execution with error (skip re-fetch if permission denied to avoid error storm after logout)
    const execution = await getWorkflowExecution(executionId);
    const nodeExecutions = execution.node_executions || [];
    if (nodeExecutions.length > 0) {
      const lastExecution = nodeExecutions[nodeExecutions.length - 1];
      lastExecution.status = 'failed';
      lastExecution.error = {
        error_code: 'EXECUTION_ERROR',
        error_message: error.message,
        error_details: { stack: error.stack }
      };
      lastExecution.completed_at = new Date(); // Use regular Date instead of serverTimestamp() (not supported in arrays)
      
      const executionRef = doc(db, 'workflow_executions', executionId);
      await updateDoc(executionRef, {
        node_executions: nodeExecutions,
        status: 'failed',
        updated_at: serverTimestamp()
      });
    }
    
    throw error;
  }
}

/**
 * Execute start node
 */
async function executeStartNode(execution, node, userId) {
  // Start nodes initialize variables and move to first connected node
  const workflowDefinition = await workflowService.getWorkflowDefinition(execution.workflow_definition_id);
  const edges = workflowDefinition.edges || [];
  
  // Find first edge from start node
  const firstEdge = edges.find(edge => edge.source_node_id === node.node_id);
  
  if (!firstEdge) {
    throw new Error('Start node must have at least one outgoing edge');
  }
  
  return {
    next_node_id: firstEdge.target_node_id
  };
}

/**
 * Execute task node.
 * Protocol can be set on the node (protocol_id or configuration.protocolId) or at workflow level (workflowDefinition.configuration.protocolId / protocol_id).
 */
async function executeTaskNode(execution, node, userId, workflowDefinition = null) {
  // Resolve protocol: node first, then workflow level (so "protocol set at workflow level" works)
  const protocolId = node.protocol_id
    || node.configuration?.protocolId
    || workflowDefinition?.configuration?.protocolId
    || workflowDefinition?.protocol_id;

  // Check timeout
  const timeoutMinutes = node.configuration?.timeout_minutes || 0;
  if (timeoutMinutes > 0) {
    const nodeExecution = execution.node_executions?.find(ne => ne.node_id === node.node_id && ne.status === 'running');
    if (nodeExecution) {
      const startedAt = nodeExecution.started_at?.toMillis() || Date.now();
      const elapsedMinutes = Math.floor((Date.now() - startedAt) / 60000);
      
      if (elapsedMinutes >= timeoutMinutes) {
        // Timeout exceeded - escalate
        await escalateNodeExecution(execution.id, node.node_id, userId, 'timeout');
        throw new Error(`Task node timeout: ${timeoutMinutes} minutes exceeded`);
      }
    }
  }
  
  // Check if approval is required
  if (node.configuration?.requires_approval) {
    // Check if already approved
    const approval = execution.approvals?.find(a => a.node_id === node.node_id);
    
    if (!approval) {
      // Task requires approval - wait for approval
      return {
        next_node_id: null, // Wait for approval
        requires_approval: true,
        approval_roles: node.configuration.approval_roles || [],
        approval_status: 'pending'
      };
    }
  }
  
  // Idempotency: only create tasks the first time this task node runs in this execution.
  // If the workflow loops back to the same node, skip creation to avoid duplicate tasks (e.g. 72 from 3 protocols).
  const alreadyRanThisTaskNode = (execution.node_executions || []).some(
    ne => ne.node_id === node.node_id && ne.status === 'completed'
  );
  if (alreadyRanThisTaskNode) {
    const wf = workflowDefinition || await workflowService.getWorkflowDefinition(execution.workflow_definition_id);
    const nextEdge = wf?.edges?.find(edge => edge.source_node_id === node.node_id);
    return {
      next_node_id: nextEdge?.target_node_id ?? null,
      task_id: null,
      task_execution_id: null
    };
  }

  // Check if task has template or protocol
  let taskId = null;
  let taskExecutionId = null;
  
  if (node.task_template_id) {
    // Create task from template
    try {
      // TODO: Implement task creation from template via taskService
      console.log('Creating task from template:', node.task_template_id);
      // const task = await taskService.createTaskFromTemplate(node.task_template_id, {
      //   context: execution.variables,
      //   location_id: execution.location_id,
      //   department_id: execution.department_id,
      //   patient_id: execution.patient_id
      // });
      // taskId = task.id;
    } catch (error) {
      console.error('Error creating task from template:', error);
      throw error;
    }
  } else if (protocolId) {
    // Create task(s) from protocol; assignment comes from protocol assignmentRules (or node override)
    try {
      const context = {
        ...(execution.variables || {}),
        workflowExecutionId: execution.id,
        workflowName: execution.workflow_name || execution.workflow_definition_id
      };
      const scheduledStart = new Date();
      const assignedToGroup = node.configuration?.assigned_to_group || node.assigned_to_group || null;
      const assignedTo = node.configuration?.assigned_to || node.assigned_to || null;
      // Only override assignment when the workflow node explicitly sets group or user; otherwise protocol-level assignment (e.g. group in applicability/assignment) is used
      const assignmentOverride = (assignedToGroup || assignedTo) ? {
        assignedToGroup: assignedToGroup || null,
        assignedTo: assignedTo || null,
        assignedToName: null
      } : null;
      const taskResults = await taskService.generateTasksFromProtocol(
        protocolId,
        context,
        scheduledStart,
        userId,
        assignmentOverride
      );
      const createdIds = Array.isArray(taskResults) ? taskResults.map(r => r.taskId || r).filter(Boolean) : [taskResults?.taskId || taskResults].filter(Boolean);
      if (createdIds.length > 0) {
        taskId = createdIds[0];
        console.log('Created task(s) from protocol:', protocolId, createdIds.length, createdIds);

        // Find next node before returning so we can save it for later resumption
        const wfForWait = workflowDefinition || await workflowService.getWorkflowDefinition(execution.workflow_definition_id);
        const nextEdgeForWait = (wfForWait?.edges || []).find(e => e.source_node_id === node.node_id);

        // Persist the pending task IDs and the next node to the execution document so
        // checkAndResumeWorkflowExecution can advance the workflow when all tasks complete.
        const executionRef = doc(db, 'workflow_executions', execution.id);
        await updateDoc(executionRef, {
          pending_task_ids: createdIds,
          pending_task_node_id: node.node_id,
          pending_next_node_id: nextEdgeForWait?.target_node_id || null,
          'execution_trust.layer_1.task_count_expected': createdIds.length,
          updated_at: serverTimestamp()
        });

        return {
          next_node_id: null, // Do NOT advance — wait for tasks to be completed
          waiting_for_tasks: true,
          task_id: taskId,
          task_execution_id: taskExecutionId
        };
      } else {
        console.warn('Workflow task node: protocol produced 0 tasks. Check protocol has steps/actions with "Generate task" or protocol-level "Generate task".');
      }
    } catch (error) {
      console.error('Error creating task from protocol:', error);
      throw error;
    }
  } else {
    // Task node has no protocol (and no template) – no tasks will be created
    console.warn(
      'Workflow task node has no protocol selected. No tasks will be created. ' +
      'Open the workflow in Visual Designer, select this task node, and choose a Protocol in the config panel.'
    );
  }
  
  // No tasks were created — find and return next node immediately so the workflow continues
  const wf = workflowDefinition || await workflowService.getWorkflowDefinition(execution.workflow_definition_id);
  const edges = wf?.edges || [];
  const nextEdge = edges.find(edge => edge.source_node_id === node.node_id);
  
  return {
    next_node_id: nextEdge?.target_node_id || null,
    task_id: taskId,
    task_execution_id: taskExecutionId
  };
}

/**
 * Execute decision node
 */
async function executeDecisionNode(execution, node, userId) {
  // Evaluate decision logic
  const decisionLogic = node.decision_logic || {};
  const variables = execution.variables || {};
  
  // Find matching condition
  let selectedNodeId = decisionLogic.default_node_id;
  
  if (decisionLogic.condition_type === 'simple' || decisionLogic.condition_type === 'complex') {
    for (const condition of decisionLogic.conditions || []) {
      if (evaluateCondition(condition, variables)) {
        selectedNodeId = condition.next_node_id;
        break;
      }
    }
  } else if (decisionLogic.condition_type === 'risk_based') {
    // Risk-based decision - use risk level from context
    // TODO: Implement risk-based decision logic
    selectedNodeId = decisionLogic.default_node_id;
  }
  
  return {
    next_node_id: selectedNodeId,
    decision_result: {
      evaluated_conditions: decisionLogic.conditions || [],
      selected_path: selectedNodeId,
      selected_node_id: selectedNodeId
    }
  };
}

/**
 * Evaluate condition
 */
function evaluateCondition(condition, variables) {
  const value = variables[condition.field];
  
  switch (condition.operator) {
    case 'equals':
      return value === condition.value;
    case 'not_equals':
      return value !== condition.value;
    case 'greater_than':
      return value > condition.value;
    case 'less_than':
      return value < condition.value;
    case 'contains':
      return String(value).includes(String(condition.value));
    default:
      return false;
  }
}

/**
 * Execute parallel node
 */
async function executeParallelNode(execution, node, userId) {
  // Parallel nodes execute multiple branches concurrently
  const workflowDefinition = await workflowService.getWorkflowDefinition(execution.workflow_definition_id);
  const edges = workflowDefinition.edges || [];
  
  // Find edges from parallel node
  const parallelEdges = edges.filter(edge => edge.source_node_id === node.node_id);
  
  if (parallelEdges.length === 0) {
    throw new Error('Parallel node must have at least one outgoing edge');
  }
  
  // Get synchronization mode (default: all)
  const synchronizationMode = node.configuration?.synchronization || 'all';
  
  // Create parallel execution record
  const parallelExecution = {
    branches: parallelEdges.map((edge, index) => ({
      branch_id: `branch_${index + 1}`,
      target_node_id: edge.target_node_id,
      status: 'running',
      started_at: new Date(), // Use regular Date instead of serverTimestamp() (not supported in arrays)
      completed_at: null,
      error: null
    })),
    synchronization_mode: synchronizationMode,
    synchronization_status: 'waiting',
    completed_branches: 0,
    total_branches: parallelEdges.length
  };
  
  // Execute all branches concurrently
  const branchPromises = parallelEdges.map(async (edge, index) => {
    try {
      // Execute branch node
      await executeNode(execution.id, edge.target_node_id, userId);
      
      // Mark branch as completed
      const executionRef = doc(db, 'workflow_executions', execution.id);
      const currentExecution = await getWorkflowExecution(execution.id);
      const nodeExecutions = currentExecution.node_executions || [];
      const lastNodeExecution = nodeExecutions[nodeExecutions.length - 1];
      
      if (lastNodeExecution) {
        const parallelExec = lastNodeExecution.parallel_execution || parallelExecution;
        const branchIndex = parallelExec.branches.findIndex(b => b.branch_id === `branch_${index + 1}`);
        
        if (branchIndex >= 0) {
          parallelExec.branches[branchIndex].status = 'completed';
          parallelExec.branches[branchIndex].completed_at = new Date(); // Use regular Date instead of serverTimestamp() (not supported in arrays)
          parallelExec.completed_branches = (parallelExec.completed_branches || 0) + 1;
          
          // Check if synchronization is complete
          if (shouldSynchronize(parallelExec)) {
            parallelExec.synchronization_status = 'synchronized';
            
            // Find next node after parallel node
            const nextNodeId = findNextNodeAfterParallel(workflowDefinition, node.node_id);
            
            await updateDoc(executionRef, {
              current_node_id: nextNodeId || execution.current_node_id,
              updated_at: serverTimestamp()
            });
            
            if (nextNodeId) {
              await executeNode(execution.id, nextNodeId, userId);
            }
          }
          
          await updateDoc(executionRef, {
            node_executions: nodeExecutions,
            updated_at: serverTimestamp()
          });
        }
      }
    } catch (error) {
      console.error(`Error executing parallel branch ${index + 1}:`, error);
      // Mark branch as failed
      const executionRef = doc(db, 'workflow_executions', execution.id);
      const currentExecution = await getWorkflowExecution(execution.id);
      const nodeExecutions = currentExecution.node_executions || [];
      const lastNodeExecution = nodeExecutions[nodeExecutions.length - 1];
      
      if (lastNodeExecution && lastNodeExecution.parallel_execution) {
        const branchIndex = lastNodeExecution.parallel_execution.branches.findIndex(
          b => b.branch_id === `branch_${index + 1}`
        );
        
        if (branchIndex >= 0) {
          lastNodeExecution.parallel_execution.branches[branchIndex].status = 'failed';
          lastNodeExecution.parallel_execution.branches[branchIndex].error = {
            error_code: 'BRANCH_EXECUTION_ERROR',
            error_message: error.message
          };
          
          await updateDoc(executionRef, {
            node_executions: nodeExecutions,
            updated_at: serverTimestamp()
          });
        }
      }
    }
  });
  
  // Wait for branches based on synchronization mode
  if (synchronizationMode === 'all') {
    await Promise.all(branchPromises);
  } else if (synchronizationMode === 'any') {
    await Promise.race(branchPromises);
  } else if (synchronizationMode === 'first') {
    await Promise.race(branchPromises);
  }
  
  return {
    next_node_id: null, // Handled by branch completion
    parallel_execution: parallelExecution
  };
}

/**
 * Check if parallel branches should synchronize
 */
function shouldSynchronize(parallelExecution) {
  const mode = parallelExecution.synchronization_mode || 'all';
  const completed = parallelExecution.completed_branches || 0;
  const total = parallelExecution.total_branches || 0;
  
  if (mode === 'all') {
    return completed === total;
  } else if (mode === 'any' || mode === 'first') {
    return completed >= 1;
  }
  
  return false;
}

/**
 * Find next node after parallel node
 */
function findNextNodeAfterParallel(workflowDefinition, parallelNodeId) {
  // Find all edges from parallel node
  const parallelEdges = workflowDefinition.edges?.filter(
    edge => edge.source_node_id === parallelNodeId
  ) || [];
  
  if (parallelEdges.length === 0) {
    return null;
  }
  
  // Find the first target node
  const firstTargetNode = parallelEdges[0].target_node_id;
  
  // Find edges from that node (should lead to synchronization point)
  const nextEdges = workflowDefinition.edges?.filter(
    edge => edge.source_node_id === firstTargetNode
  ) || [];
  
  // Return the first next node (or null if no synchronization point)
  return nextEdges.length > 0 ? nextEdges[0].target_node_id : null;
}

/**
 * Execute end node
 */
async function executeEndNode(execution, node, userId) {
  // End nodes complete the workflow
  const executionRef = doc(db, 'workflow_executions', execution.id);
  
  const startedAt = execution.started_at?.toMillis() || Date.now();
  const completedAt = Date.now();
  const durationMinutes = Math.floor((completedAt - startedAt) / 60000);
  
  await updateDoc(executionRef, {
    status: 'completed',
    completed_at: serverTimestamp(),
    duration_minutes: durationMinutes,
    outcome: 'success',
    updated_at: serverTimestamp(),
    execution_log: [
      ...(execution.execution_log || []),
      {
        timestamp: new Date(), // Use regular Date instead of serverTimestamp() (not supported in arrays)
        event: 'execution_completed',
        details: {
          node_id: node.node_id,
          user_id: userId
        }
      }
    ]
  });
  
  return {
    next_node_id: null // No next node - workflow complete
  };
}

/**
 * Execute integration node.
 * Runs automated checks against external data sources (Firestore collections).
 * Non-blocking — always returns next_node_id regardless of check outcome.
 */
async function executeIntegrationNode(execution, node, userId) {
  const subtype = node.integration_subtype || node.configuration?.integration_subtype || 'learning_check';
  const workflowDefinition = await workflowService.getWorkflowDefinition(execution.workflow_definition_id);
  const edges = workflowDefinition.edges || [];
  const nextEdge = edges.find(edge => edge.source_node_id === node.node_id);

  if (subtype === 'learning_check') {
    await executeLearningCheck(execution, node);
  }

  return {
    next_node_id: nextEdge?.target_node_id || null
  };
}

/**
 * Execute notification node.
 * Creates a persistent alert in risk_alerts for configured recipients.
 * Non-blocking — always returns next_node_id.
 */
async function executeNotificationNode(execution, node, userId) {
  const workflowDefinition = await workflowService.getWorkflowDefinition(execution.workflow_definition_id);
  const edges = workflowDefinition.edges || [];
  const nextEdge = edges.find(edge => edge.source_node_id === node.node_id);

  const config = node.notification_config || node.configuration?.notification_config || {};
  const recipients = config.recipients || [];
  const recipientUserIds = recipients.map(r => r.userId).filter(Boolean);

  if (recipientUserIds.length === 0) {
    console.warn(`[NotificationNode] Node ${node.node_id} has no recipients — skipping alert creation`);
  } else {
    try {
      await alertService.create({
        alertType: 'workflow_notification',
        severity: config.severity || 'MEDIUM',
        title: config.title || 'Notificare workflow',
        message: config.message || '',
        recipientUsers: recipientUserIds,
        workflowExecutionId: execution.id,
        workflowName: execution.workflow_name || '',
        organizationId: execution.organization_id || null
      });
      console.log(`✅ [NotificationNode] Alert created for execution ${execution.id} — ${recipientUserIds.length} recipient(s)`);
    } catch (err) {
      console.warn('[NotificationNode] alertService.create failed (non-blocking):', err?.message || err);
    }
  }

  return {
    next_node_id: nextEdge?.target_node_id || null
  };
}

/**
 * Execute ATP check node.
 * Creează un task ATP cu suprafață aleatorie sau configurată pe nod.
 * source: 'workflow' — generat din context de workflow cu execution_code pentru trasabilitate.
 * Workflow-ul se suspendă până la completarea task-ului.
 */
async function executeATPCheckNode(execution, node, userId) {
  const workflowDefinition = await workflowService.getWorkflowDefinition(execution.workflow_definition_id);
  const edges = workflowDefinition.edges || [];
  const nextEdge = edges.find(edge => edge.source_node_id === node.node_id);

  const config = node.atp_config || node.configuration?.atp_config || {};
  const orgId = execution.organization_id;

  let surfaceTypeId = config.surface_type_id || null;
  let surfaceTypeName = config.surface_type_name || '';

  if (!surfaceTypeId) {
    try {
      const surfaces = await surfaceTypeService.getSurfaceTypes(orgId);
      if (surfaces.length > 0) {
        const random = surfaces[Math.floor(Math.random() * surfaces.length)];
        surfaceTypeId = random.id;
        surfaceTypeName = random.name;
      }
    } catch (e) {
      console.warn('[executeATPCheckNode] Could not load surface types:', e);
    }
  }

  const taskData = {
    name: `Test ATP — ${config.room_name || ''} — ${surfaceTypeName}`,
    type: 'atp_check',
    status: 'pending',
    assignedToGroup: config.assigned_group_id || null,
    assignedToGroupName: config.assigned_group_name || '',
    assignedTo: config.assigned_user_id || null,
    assignedToName: config.assigned_user_name || null,
    atp_context: {
      room_id: config.room_id || execution.location_id || null,
      room_name: config.room_name || '',
      department_id: config.department_id || execution.department_id || null,
      department_name: config.department_name || '',
      surface_type_id: surfaceTypeId,
      surface_type_name: surfaceTypeName,
      risk_level: config.risk_level || 'Medium',
      scheduled_by: 'workflow'
    },
    source: 'workflow',
    source_id: execution.id,
    source_ref: execution.execution_code || null,
    organization_id: orgId,
    workflow_execution_id: execution.id,
    workflow_node_id: node.node_id,
    triggeredByWorkflow: execution.id,
    scheduledStart: serverTimestamp(),
    scheduledEnd: Timestamp.fromMillis(Date.now() + 4 * 60 * 60 * 1000),
    created_at: serverTimestamp(),
    created_by: userId,
    updated_at: serverTimestamp(),
    updated_by: userId
  };

  const taskRef = await addDoc(collection(db, 'task_instances'), taskData);
  const taskId = taskRef.id;

  const executionRef = doc(db, 'workflow_executions', execution.id);
  await updateDoc(executionRef, {
    pending_task_ids: [taskId],
    pending_task_node_id: node.node_id,
    pending_next_node_id: nextEdge?.target_node_id || null,
    'execution_trust.layer_1.task_count_expected': 1,
    updated_at: serverTimestamp()
  });

  return {
    next_node_id: null,
    waiting_for_tasks: true,
    task_id: taskId
  };
}

/**
 * Learning check — verifies quiz completion for each task executor.
 * Source of truth: execution_trust.layer_1.declared_executions (populated by ETF).
 * Reads quiz_completions: doc ID = {staffId}_{protocolId}_{userGroupId}
 */
async function executeLearningCheck(execution, node) {
  try {
    const fresh = await getWorkflowExecution(execution.id);
    const declaredExecutions = fresh?.execution_trust?.layer_1?.declared_executions || [];
    const taskResults = [];

    for (const declared of declaredExecutions) {
      const taskId = declared.task_id;
      const staffId = declared.executor_id || null;
      const protocolId = declared.protocol_id || null;

      if (!staffId || !protocolId) {
        taskResults.push({
          task_id: taskId,
          staff_id: staffId,
          protocol_id: protocolId,
          user_group_id: null,
          quiz_completion_id: null,
          passed: false,
          score: null,
          completed_at: null,
          reason: !staffId ? 'no_assignee' : 'no_protocol'
        });
        continue;
      }

      let userGroupId = null;
      try {
        const taskSnap = await getDoc(doc(db, 'task_instances', taskId));
        if (taskSnap.exists()) {
          userGroupId = taskSnap.data().assignedToGroup || null;
        }
      } catch (e) {
        console.warn(`[LearningCheck] Could not read task ${taskId}:`, e);
      }

      if (!userGroupId) {
        taskResults.push({
          task_id: taskId,
          staff_id: staffId,
          protocol_id: protocolId,
          user_group_id: null,
          quiz_completion_id: null,
          passed: false,
          score: null,
          completed_at: null,
          reason: 'no_group'
        });
        continue;
      }

      const quizCompletionId = `${staffId}_${protocolId}_${userGroupId}`;
      try {
        const completionSnap = await getDoc(doc(db, 'quiz_completions', quizCompletionId));

        if (completionSnap.exists()) {
          const data = completionSnap.data();
          taskResults.push({
            task_id: taskId,
            staff_id: staffId,
            protocol_id: protocolId,
            user_group_id: userGroupId,
            quiz_completion_id: quizCompletionId,
            passed: true,
            score: data.finalScore ?? null,
            completed_at: data.completedAt || null,
            reason: null
          });
        } else {
          taskResults.push({
            task_id: taskId,
            staff_id: staffId,
            protocol_id: protocolId,
            user_group_id: userGroupId,
            quiz_completion_id: null,
            passed: false,
            score: null,
            completed_at: null,
            reason: 'no_completion'
          });
        }
      } catch (e) {
        console.warn(`[LearningCheck] Quiz completion query failed for ${quizCompletionId}:`, e);
        taskResults.push({
          task_id: taskId,
          staff_id: staffId,
          protocol_id: protocolId,
          user_group_id: userGroupId,
          quiz_completion_id: null,
          passed: false,
          score: null,
          completed_at: null,
          reason: 'query_error'
        });
      }
    }

    populateLearningCheckResult(execution.id, taskResults)
      .catch(err => console.warn('[LearningCheck] populateLearningCheckResult failed:', err));
  } catch (err) {
    console.warn('[executeLearningCheck] Failed (non-blocking):', err?.message || err);
  }
}

/**
 * Move to next node
 */
async function moveToNextNode(executionId, nextNodeId, userId) {
  const executionRef = doc(db, 'workflow_executions', executionId);
  
  await updateDoc(executionRef, {
    current_node_id: nextNodeId,
    updated_at: serverTimestamp()
  });
  
  // Execute next node
  await executeNode(executionId, nextNodeId, userId);
}

/**
 * Check whether all pending tasks for a workflow execution have completed and, if so,
 * advance the execution to the next node.
 *
 * Called automatically from taskService.completeTask() (via dynamic import) so that
 * the workflow resumes as soon as the last task of a task-node is finished.
 */
export async function checkAndResumeWorkflowExecution(executionId, userId) {
  try {
    const execution = await getWorkflowExecution(executionId);
    if (!execution) return;

    const pendingTaskIds = execution.pending_task_ids;
    const nextNodeId = execution.pending_next_node_id;

    if (!pendingTaskIds || pendingTaskIds.length === 0 || !nextNodeId) return;

    // Check completion status of every pending task
    const taskInstancesRef = collection(db, 'task_instances');
    const taskDocs = await getDocs(
      query(taskInstancesRef, where('__name__', 'in', pendingTaskIds))
    );

    const allDone = taskDocs.docs.every(d => {
      const s = d.data().status;
      return s === 'completed' || s === 'cancelled';
    });

    if (!allDone) return; // Still waiting

    console.log(`▶️ All tasks done for execution ${executionId} — resuming workflow at node ${nextNodeId}`);

    const executionRef = doc(db, 'workflow_executions', executionId);

    // Mark the waiting task node as completed in node_executions array
    const latestExecution = await getWorkflowExecution(executionId);
    const nodeExecutions = latestExecution.node_executions || [];
    const waitingNodeIndex = nodeExecutions.findLastIndex(
      ne => ne.node_id === execution.pending_task_node_id && ne.status === 'waiting'
    );
    if (waitingNodeIndex >= 0) {
      nodeExecutions[waitingNodeIndex] = {
        ...nodeExecutions[waitingNodeIndex],
        completed_at: new Date(),
        status: 'completed'
      };
    }

    await updateDoc(executionRef, {
      current_node_id: nextNodeId,
      pending_task_ids: [],
      pending_task_node_id: null,
      pending_next_node_id: null,
      node_executions: nodeExecutions,
      updated_at: serverTimestamp()
    });

    await executeNode(executionId, nextNodeId, userId);
  } catch (err) {
    console.error('checkAndResumeWorkflowExecution error:', err);
  }
}

/**
 * Cancel workflow execution
 */
export async function cancelWorkflowExecution(executionId, userId) {
  try {
    const executionRef = doc(db, 'workflow_executions', executionId);
    const execution = await getWorkflowExecution(executionId);
    
    if (execution.status === 'completed' || execution.status === 'cancelled') {
      throw new Error(`Cannot cancel execution in status: ${execution.status}`);
    }
    
    await updateDoc(executionRef, {
      status: 'cancelled',
      updated_at: serverTimestamp(),
      execution_log: [
        ...(execution.execution_log || []),
        {
          timestamp: new Date(), // Use regular Date instead of serverTimestamp() (not supported in arrays)
          event: 'execution_cancelled',
          details: { user_id: userId }
        }
      ]
    });
    
    console.log(`✅ Cancelled workflow execution: ${executionId}`);
  } catch (error) {
    console.error('Error cancelling workflow execution:', error);
    throw error;
  }
}

/**
 * Approve node execution
 */
export async function approveNodeExecution(executionId, nodeId, userId, approvalNotes = '') {
  try {
    const execution = await getWorkflowExecution(executionId);
    const workflowDefinition = await workflowService.getWorkflowDefinition(execution.workflow_definition_id);
    const node = workflowDefinition.nodes?.find(n => n.node_id === nodeId);
    
    if (!node) {
      throw new Error(`Node not found: ${nodeId}`);
    }
    
    // Add approval record
    const approval = {
      node_id: nodeId,
      required_role: node.configuration?.approval_roles?.[0] || 'Management',
      approved_by: userId,
      approved_at: new Date(), // Use regular Date instead of serverTimestamp() (not supported in arrays)
      approval_notes: approvalNotes
    };
    
    const executionRef = doc(db, 'workflow_executions', executionId);
    await updateDoc(executionRef, {
      approvals: [...(execution.approvals || []), approval],
      updated_at: serverTimestamp(),
      execution_log: [
        ...(execution.execution_log || []),
        {
          timestamp: new Date(), // Use regular Date instead of serverTimestamp() (not supported in arrays)
          event: 'node_approved',
          details: {
            node_id: nodeId,
            approved_by: userId
          }
        }
      ]
    });
    
    // Continue node execution
    await executeNode(executionId, nodeId, userId);
    
    console.log(`✅ Approved node execution: ${nodeId}`);
  } catch (error) {
    console.error('Error approving node execution:', error);
    throw error;
  }
}

/**
 * Escalate node execution
 */
export async function escalateNodeExecution(executionId, nodeId, userId, reason = 'overdue') {
  try {
    const execution = await getWorkflowExecution(executionId);
    const workflowDefinition = await workflowService.getWorkflowDefinition(execution.workflow_definition_id);
    const node = workflowDefinition.nodes?.find(n => n.node_id === nodeId);
    
    if (!node) {
      throw new Error(`Node not found: ${nodeId}`);
    }
    
    // Determine escalation target
    const escalationRoles = node.configuration?.escalation_roles || ['Management', 'IC Personnel'];
    const escalatedTo = escalationRoles[0] || 'Management';
    
    // Create escalation record
    const escalation = {
      node_id: nodeId,
      escalated_at: new Date(), // Use regular Date instead of serverTimestamp() (not supported in arrays)
      escalated_to: escalatedTo,
      escalated_by: userId,
      reason: reason,
      resolved: false
    };
    
    const executionRef = doc(db, 'workflow_executions', executionId);
    await updateDoc(executionRef, {
      escalations: [...(execution.escalations || []), escalation],
      updated_at: serverTimestamp(),
      execution_log: [
        ...(execution.execution_log || []),
        {
          timestamp: new Date(), // Use regular Date instead of serverTimestamp() (not supported in arrays)
          event: 'node_escalated',
          details: {
            node_id: nodeId,
            reason: reason,
            escalated_to: escalatedTo,
            escalated_by: userId
          }
        }
      ]
    });
    
    console.log(`⚠️ Escalated node execution: ${nodeId} (reason: ${reason})`);
    
    return escalation;
  } catch (error) {
    console.error('Error escalating node execution:', error);
    throw error;
  }
}

/**
 * Check for timeouts and escalate if needed
 */
export async function checkTimeouts() {
  try {
    // Get all running executions
    const runningExecutions = await getAllWorkflowExecutions({ status: 'running' });
    
    for (const execution of runningExecutions) {
      const workflowDefinition = await workflowService.getWorkflowDefinition(execution.workflow_definition_id);
      const currentNode = workflowDefinition.nodes?.find(n => n.node_id === execution.current_node_id);
      
      if (!currentNode) continue;
      
      // Check node timeout
      const timeoutMinutes = currentNode.configuration?.timeout_minutes || 0;
      if (timeoutMinutes > 0) {
        const nodeExecution = execution.node_executions?.find(
          ne => ne.node_id === execution.current_node_id && ne.status === 'running'
        );
        
        if (nodeExecution) {
          const startedAt = nodeExecution.started_at?.toMillis() || Date.now();
          const elapsedMinutes = Math.floor((Date.now() - startedAt) / 60000);
          
          if (elapsedMinutes >= timeoutMinutes) {
            // Check if already escalated
            const existingEscalation = execution.escalations?.find(
              e => e.node_id === execution.current_node_id && !e.resolved
            );
            
            if (!existingEscalation) {
              await escalateNodeExecution(execution.id, execution.current_node_id, 'system', 'timeout');
            }
          }
        }
      }
      
      // Check execution timeout (if configured)
      const executionTimeoutMinutes = workflowDefinition.configuration?.timeout_minutes || 0;
      if (executionTimeoutMinutes > 0 && execution.started_at) {
        const startedAt = execution.started_at.toMillis();
        const elapsedMinutes = Math.floor((Date.now() - startedAt) / 60000);
        
        if (elapsedMinutes >= executionTimeoutMinutes) {
          // Cancel execution due to timeout
          await cancelWorkflowExecution(execution.id, 'system');
          console.log(`⏱️ Execution timeout: ${execution.id}`);
        }
      }
    }
  } catch (error) {
    console.error('Error checking timeouts:', error);
    throw error;
  }
}

/**
 * Pause workflow execution
 */
export async function pauseWorkflowExecution(executionId, userId) {
  try {
    const executionRef = doc(db, 'workflow_executions', executionId);
    const execution = await getWorkflowExecution(executionId);
    
    if (execution.status !== 'running') {
      throw new Error(`Cannot pause execution in status: ${execution.status}`);
    }
    
    await updateDoc(executionRef, {
      status: 'paused',
      paused_at: serverTimestamp(),
      paused_by: userId,
      updated_at: serverTimestamp(),
      execution_log: [
        ...(execution.execution_log || []),
        {
          timestamp: new Date(), // Use regular Date instead of serverTimestamp() (not supported in arrays)
          event: 'execution_paused',
          details: { user_id: userId }
        }
      ]
    });
    
    console.log(`⏸️ Paused workflow execution: ${executionId}`);
  } catch (error) {
    console.error('Error pausing workflow execution:', error);
    throw error;
  }
}

/**
 * Resume workflow execution
 */
export async function resumeWorkflowExecution(executionId, userId) {
  try {
    const executionRef = doc(db, 'workflow_executions', executionId);
    const execution = await getWorkflowExecution(executionId);
    
    if (execution.status !== 'paused') {
      throw new Error(`Cannot resume execution in status: ${execution.status}`);
    }
    
    await updateDoc(executionRef, {
      status: 'running',
      resumed_at: serverTimestamp(),
      resumed_by: userId,
      updated_at: serverTimestamp(),
      execution_log: [
        ...(execution.execution_log || []),
        {
          timestamp: new Date(), // Use regular Date instead of serverTimestamp() (not supported in arrays)
          event: 'execution_resumed',
          details: { user_id: userId }
        }
      ]
    });
    
    // Continue execution from current node
    if (execution.current_node_id) {
      await executeNode(executionId, execution.current_node_id, userId);
    }
    
    console.log(`▶️ Resumed workflow execution: ${executionId}`);
  } catch (error) {
    console.error('Error resuming workflow execution:', error);
    throw error;
  }
}

/**
 * Resolve escalation
 */
export async function resolveEscalation(executionId, nodeId, userId, resolutionNotes = '') {
  try {
    const execution = await getWorkflowExecution(executionId);
    const escalations = execution.escalations || [];
    
    const escalationIndex = escalations.findIndex(
      e => e.node_id === nodeId && !e.resolved
    );
    
    if (escalationIndex < 0) {
      throw new Error(`No unresolved escalation found for node: ${nodeId}`);
    }
    
    escalations[escalationIndex].resolved = true;
    escalations[escalationIndex].resolved_at = new Date(); // Use regular Date instead of serverTimestamp() (not supported in arrays)
    escalations[escalationIndex].resolved_by = userId;
    escalations[escalationIndex].resolution_notes = resolutionNotes;
    
    const executionRef = doc(db, 'workflow_executions', executionId);
    await updateDoc(executionRef, {
      escalations: escalations,
      updated_at: serverTimestamp(),
      execution_log: [
        ...(execution.execution_log || []),
        {
          timestamp: new Date(), // Use regular Date instead of serverTimestamp() (not supported in arrays)
          event: 'escalation_resolved',
          details: {
            node_id: nodeId,
            resolved_by: userId
          }
        }
      ]
    });
    
    console.log(`✅ Resolved escalation for node: ${nodeId}`);
  } catch (error) {
    console.error('Error resolving escalation:', error);
    throw error;
  }
}

const workflowExecutionService = {
  createWorkflowExecution,
  startWorkflowExecution,
  getWorkflowExecution,
  getAllWorkflowExecutions,
  executeNode,
  cancelWorkflowExecution,
  pauseWorkflowExecution,
  resumeWorkflowExecution,
  approveNodeExecution,
  escalateNodeExecution,
  checkTimeouts,
  resolveEscalation
};

export default workflowExecutionService;

